/**
 * useInsightsData — VS Insights orchestration hook
 * ==================================================
 * Fetches all 5 view queries in parallel, transforms raw rows
 * into the InsightsResponse contract, and manages loading/error state.
 *
 * Phase A: Financial Health fully wired. Other tabs return honest
 * NOT_CONNECTED / empty states.
 */

import { useState, useEffect, useCallback } from 'react';
import useAppState from '../common/AppState';
import type {
  InsightsResponse,
  UseInsightsDataResult,
  ChartSeries,
  FinancialHealthSection,
  OverviewSection,
  AcquisitionSection,
  LoanPerformanceSection,
  UnderservedSection,
  InsightsMeta,
  BandMigrationPoint,
} from '../types/insightsTypes';
import { NOT_CONNECTED_REGISTRY } from '../types/insightsTypes';
import {
  fetchAllInsights,
  toMetricValue,
  toNotConnectedMetric,
  buildScoreBands,
  classifySufficiency,
  type FinancialHealthRow,
  type OverviewRow,
  type AcquisitionRow,
  type BehavioralSummary,
} from '../services/insightsService';

// ─── Portfolio Health Index ─────────────────────────────────────────────────

/**
 * Compute Portfolio Health Index (0–100) from financial health factors.
 *
 * Weights:
 *   avg_verascore       40%  (already 0–100)
 *   avg_dti             20%  (inverted: 100 - clamp(dti, 0, 100))
 *   liquid_reserves     20%  (normalize: clamp(months * 10, 0, 100))
 *   cash_flow_stability 20%  (already 0–100)
 *
 * Returns null if avg_verascore is missing (minimum required input).
 */
function computePortfolioHealthIndex(fh: FinancialHealthSection): {
  value: number | null;
  trend: number | null;
  sampleSize: number;
} {
  const score = fh.avg_verascore.value;
  if (score == null) return { value: null, trend: null, sampleSize: 0 };

  const n = fh.avg_verascore.sample_size;

  // Normalize inputs to 0–100
  const scoreNorm = Math.max(0, Math.min(100, score));
  const dtiNorm = fh.factor_breakdown.dti.value != null
    ? 100 - Math.max(0, Math.min(100, fh.factor_breakdown.dti.value))
    : scoreNorm; // fallback to score if DTI unavailable
  const reservesNorm = fh.factor_breakdown.liquid_reserves.value != null
    ? Math.max(0, Math.min(100, fh.factor_breakdown.liquid_reserves.value * 10))
    : scoreNorm;
  const cashFlowNorm = fh.factor_breakdown.cash_flow_stability.value != null
    ? Math.max(0, Math.min(100, fh.factor_breakdown.cash_flow_stability.value))
    : scoreNorm;

  const phi = Math.round(
    (scoreNorm * 0.4 + dtiNorm * 0.2 + reservesNorm * 0.2 + cashFlowNorm * 0.2) * 10
  ) / 10;

  return { value: phi, trend: fh.avg_verascore.trend, sampleSize: n };
}

// ─── Transformers ───────────────────────────────────────────────────────────

function buildFinancialHealth(rows: FinancialHealthRow[], behavioral?: BehavioralSummary): FinancialHealthSection {
  const latestDate = rows[0]?.metric_date;
  const latestRows = latestDate ? rows.filter((r) => r.metric_date === latestDate) : [];
  const totalMembers = latestRows.reduce((sum, r) => sum + (r.member_count ?? 0), 0);

  const weightedAvg = (field: keyof FinancialHealthRow): number | null => {
    if (totalMembers === 0) return null;
    let sum = 0;
    for (const r of latestRows) {
      const val = r[field] as number | null;
      if (val != null) sum += val * (r.member_count ?? 0);
    }
    return sum / totalMembers;
  };

  const avgScore = weightedAvg('avg_score');
  const avgDti = weightedAvg('avg_dti');
  const avgReserves = weightedAvg('avg_reserves');
  const avgCashFlow = weightedAvg('avg_cash_flow_stability');

  // Score trend chart: group by metric_date, compute weighted avg per date
  const byDate = new Map<string, { total: number; count: number }>();
  for (const r of rows) {
    const entry = byDate.get(r.metric_date) ?? { total: 0, count: 0 };
    if (r.avg_score != null) {
      entry.total += r.avg_score * (r.member_count ?? 0);
      entry.count += r.member_count ?? 0;
    }
    byDate.set(r.metric_date, entry);
  }
  const trendData = Array.from(byDate.entries())
    .map(([date, { total, count }]) => ({
      period: date,
      value: count > 0 ? Math.round((total / count) * 100) / 100 : 0,
    }))
    .sort((a, b) => a.period.localeCompare(b.period));

  const scoreTrendChart: ChartSeries = {
    label: 'Avg VeraScore',
    data: trendData,
    status: 'live',
  };

  const trend =
    trendData.length >= 2
      ? trendData[trendData.length - 1].value - trendData[trendData.length - 2].value
      : null;

  const src = 'lender_financial_health_v';

  // Score band migration: band counts per date (for ScoreBandMigrationChart)
  const migrationData: BandMigrationPoint[] = rows
    .map((r) => ({
      period: r.metric_date,
      band: r.score_band,
      count: r.member_count ?? 0,
    }))
    .sort((a, b) => a.period.localeCompare(b.period) || a.band.localeCompare(b.band));

  return {
    avg_verascore: toMetricValue(avgScore, totalMembers, src, 'higher_is_better', 'live', trend),
    score_consistency: toNotConnectedMetric('algorithm', 'higher_is_better'),
    member_satisfaction: toNotConnectedMetric('survey', 'higher_is_better'),
    stress_reduction: toNotConnectedMetric('survey', 'higher_is_better'),
    // P2B: High financial stress count from survey behavioral data
    high_financial_stress_count: behavioral && behavioral.high_financial_stress_count > 0
      ? toMetricValue(behavioral.high_financial_stress_count, behavioral.high_financial_stress_count, 'vs_psychographic_features', 'lower_is_better', 'live')
      : toMetricValue(0, 0, 'vs_psychographic_features', 'lower_is_better', 'not_connected'),
    // P4C: Declining VeraScore count from CoreMath segment (always live — 0 is a valid signal)
    declining_score_90d_count: toMetricValue(
      behavioral?.declining_score_90d_count ?? 0,
      totalMembers,
      'vs_member_segment_map',
      'lower_is_better',
      'live',
    ),
    // P4C-2A: High DTI count from CoreMath segment (always live — 0 is a valid signal)
    dti_high_count: toMetricValue(
      behavioral?.dti_high_count ?? 0,
      totalMembers,
      'vs_member_segment_map',
      'lower_is_better',
      'live',
    ),
    // P4C-2B: Low liquid reserves count from CoreMath segment (always live — 0 is a valid signal)
    liquidity_months_critical_count: toMetricValue(
      behavioral?.liquidity_months_critical_count ?? 0,
      totalMembers,
      'vs_member_segment_map',
      'lower_is_better',
      'live',
    ),
    score_distribution: buildScoreBands(latestRows),
    score_trend_chart: scoreTrendChart,
    score_band_migration: migrationData,
    factor_breakdown: {
      dti: toMetricValue(avgDti, totalMembers, src, 'lower_is_better'),
      liquid_reserves: toMetricValue(avgReserves, totalMembers, src, 'higher_is_better'),
      cash_flow_stability: toMetricValue(avgCashFlow, totalMembers, src, 'higher_is_better'),
      savings: toNotConnectedMetric('algorithm', 'higher_is_better'),
      debt: toNotConnectedMetric('algorithm', 'lower_is_better'),
      payment_history: toNotConnectedMetric('algorithm', 'higher_is_better'),
    },
    dti_segment_warning: true,
  };
}

function buildOverview(
  ovRows: OverviewRow[],
  acqRows: AcquisitionRow[],
  fhRows: FinancialHealthRow[],
  financialHealth: FinancialHealthSection,
): OverviewSection {
  const ncChart: ChartSeries = { label: '', data: [], status: 'not_connected' };
  const src = 'lender_insights_overview_v';

  // --- total_members from overview view (metric_key = 'total_members') ---
  const tmRow = ovRows.find((r) => r.metric_key === 'total_members');
  const totalMembers = tmRow?.current_value ?? null;
  const tmSample = totalMembers ?? 0;

  // --- avg_verascore from financial health (already computed, pass latest) ---
  const latestFhDate = fhRows[0]?.metric_date;
  const latestFhRows = latestFhDate ? fhRows.filter((r) => r.metric_date === latestFhDate) : [];
  const fhTotal = latestFhRows.reduce((s, r) => s + (r.member_count ?? 0), 0);
  let avgScore: number | null = null;
  if (fhTotal > 0) {
    let sum = 0;
    for (const r of latestFhRows) {
      if (r.avg_score != null) sum += r.avg_score * (r.member_count ?? 0);
    }
    avgScore = sum / fhTotal;
  }

  // --- member_growth_count from acquisition view (metric_key = 'total_members' monthly) ---
  const growthRows = acqRows
    .filter((r) => r.metric_key === 'total_members')
    .sort((a, b) => a.metric_month.localeCompare(b.metric_month));
  const memberGrowthChart: ChartSeries = {
    label: 'Total Members',
    data: growthRows.map((r) => ({
      period: r.metric_month,
      value: r.metric_value ?? 0,
    })),
    status: growthRows.length > 0 ? 'partial' : 'not_connected',
  };

  // --- date_range from latest overview row ---
  const allDates = ovRows.map((r) => r.metric_date).filter(Boolean).sort();
  const dateStart = allDates[0] ?? '';
  const dateEnd = allDates[allDates.length - 1] ?? '';
  const fmtDate = (d: string) => {
    if (!d) return '';
    try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
    catch { return d; }
  };

  // --- Portfolio Health Index ---
  const phi = computePortfolioHealthIndex(financialHealth);
  const phiMetric = toMetricValue(
    phi.value, phi.sampleSize, 'computed (verascore+dti+reserves+cashflow)',
    'higher_is_better', phi.value != null ? 'live' : 'not_connected', phi.trend,
  );

  return {
    total_members: toMetricValue(totalMembers, tmSample, src, 'higher_is_better', totalMembers != null ? 'live' : 'not_connected'),
    avg_verascore: toMetricValue(avgScore, fhTotal, 'lender_financial_health_v', 'higher_is_better', avgScore != null ? 'live' : 'not_connected'),
    revenue_per_member: toNotConnectedMetric('core_banking'),
    member_satisfaction: toNotConnectedMetric('survey'),
    portfolio_health_index: phiMetric,
    member_growth_count: memberGrowthChart,
    revenue_growth: ncChart,
    loan_funnel: toNotConnectedMetric('los'),
    loan_portfolio: toNotConnectedMetric('los'),
    date_range: {
      start: dateStart,
      end: dateEnd,
      label: dateStart && dateEnd ? `${fmtDate(dateStart)} — ${fmtDate(dateEnd)}` : 'No data available',
    },
  };
}

function buildAcquisition(
  acqRows: AcquisitionRow[],
  overviewTotalMembers: number | null,
): AcquisitionSection {
  const ncChart: ChartSeries = { label: '', data: [], status: 'not_connected' };
  const src = 'lender_acquisition_v';

  // --- new_members: latest month metric_key = 'new_members' (PARTIAL) ---
  const newMemberRows = acqRows
    .filter((r) => r.metric_key === 'new_members')
    .sort((a, b) => b.metric_month.localeCompare(a.metric_month));
  const latestNewMembers = newMemberRows[0]?.metric_value ?? null;
  const prevNewMembers = newMemberRows[1]?.metric_value ?? null;
  const nmTrend = latestNewMembers != null && prevNewMembers != null && prevNewMembers > 0
    ? Math.round(((latestNewMembers - prevNewMembers) / prevNewMembers) * 1000) / 10
    : null;

  // --- total_member_base: from acquisition view or overview total_members ---
  const tmRows = acqRows
    .filter((r) => r.metric_key === 'total_members')
    .sort((a, b) => b.metric_month.localeCompare(a.metric_month));
  const latestTotalMembers = tmRows[0]?.metric_value ?? overviewTotalMembers;
  const prevTotalMembers = tmRows[1]?.metric_value ?? null;
  const tmTrend = latestTotalMembers != null && prevTotalMembers != null && prevTotalMembers > 0
    ? Math.round(((latestTotalMembers - prevTotalMembers) / prevTotalMembers) * 1000) / 10
    : null;

  // --- monthly_acquisition_chart: new_members over time (PARTIAL) ---
  const monthlyAcqData = newMemberRows
    .sort((a, b) => a.metric_month.localeCompare(b.metric_month))
    .map((r) => ({ period: r.metric_month, value: r.metric_value ?? 0 }));
  const monthlyAcqChart: ChartSeries = {
    label: 'New Members',
    data: monthlyAcqData,
    status: monthlyAcqData.length > 0 ? 'partial' : 'not_connected',
  };

  return {
    new_members: toMetricValue(latestNewMembers, latestNewMembers ?? 0, src, 'higher_is_better', latestNewMembers != null ? 'partial' : 'not_connected', nmTrend),
    total_member_base: toMetricValue(latestTotalMembers, latestTotalMembers ?? 0, src, 'higher_is_better', latestTotalMembers != null ? 'live' : 'not_connected', tmTrend),
    revenue_per_member: toNotConnectedMetric('core_banking'),
    avg_services: toNotConnectedMetric('core_banking'),
    monthly_acquisition_chart: monthlyAcqChart,
    revenue_trend_chart: ncChart,
    services_chart: ncChart,
    tenure_chart: ncChart,
  };
}

function buildLoanPerformanceStub(): LoanPerformanceSection {
  return {
    conversion_rate: toNotConnectedMetric('los', 'higher_is_better'),
    applications: toNotConnectedMetric('los', 'higher_is_better'),
    avg_decision_time: toNotConnectedMetric('los', 'lower_is_better'),
    default_rate: toNotConnectedMetric('los', 'lower_is_better'),
    not_connected_info: NOT_CONNECTED_REGISTRY.los,
  };
}

// ─── Main Hook ──────────────────────────────────────────────────────────────

export function useInsightsData(): UseInsightsDataResult {
  const { orgUser } = useAppState();
  const orgId = orgUser?.org_id ?? '';

  const [data, setData] = useState<InsightsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchAll = useCallback(async () => {
    if (!orgId) {
      setLoading(false);
      setError(new Error('No tenant context available'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Single API call — server handles tenant resolution + service_role queries
      if (import.meta.env.DEV) console.log(`📊 useInsightsData: fetching via API (org_id=${orgId})`);

      const { data: apiData, error: apiError } = await fetchAllInsights();

      if (apiError || !apiData) {
        throw apiError || new Error('No data returned from insights API');
      }

      const fhRows = apiData.financial_health || [];
      const ovRows = apiData.overview || [];
      const acqRows = apiData.acquisition || [];
      const usRows = (apiData.underserved || []) as FinancialHealthRow[];

      // Build response — all sections live/honest
      const financialHealth = buildFinancialHealth(fhRows, apiData.behavioral);
      const overview = buildOverview(ovRows, acqRows, fhRows, financialHealth);
      const acquisition = buildAcquisition(acqRows, overview.total_members.value);
      const loanPerformance = buildLoanPerformanceStub();
      const underserved = buildUnderserved(usRows);

      // Compute freshness from financial health data
      const latestDate = fhRows[0]?.metric_date;
      const freshnessHours = latestDate
        ? (Date.now() - new Date(latestDate).getTime()) / (1000 * 60 * 60)
        : Infinity;

      const totalSampleSize = financialHealth.avg_verascore.sample_size;

      const meta: InsightsMeta = {
        tenant_id: apiData.tenant_id,
        fetched_at: apiData.meta?.fetched_at || new Date().toISOString(),
        freshness_hours: Math.round(freshnessHours * 10) / 10,
        total_sample_size: totalSampleSize,
        sufficiency: classifySufficiency(totalSampleSize),
      };

      setData({
        overview,
        acquisition,
        loan_performance: loanPerformance,
        underserved,
        financial_health: financialHealth,
        // P3: Pass raw behavioral counts for the rule engine
        behavioral: apiData.behavioral,
        meta,
      });
    } catch (err) {
      if (import.meta.env.DEV) console.error('❌ useInsightsData error:', err);
      setError(err instanceof Error ? err : new Error('Unknown error'));
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  return { data, loading, error, refetch: fetchAll };
}

export default useInsightsData;

function buildUnderserved(usRows: FinancialHealthRow[]): UnderservedSection {
  // Build live score band data from lender_underserved_v
  const latestDate = usRows[0]?.metric_date;
  const latestRows = latestDate ? usRows.filter((r) => r.metric_date === latestDate) : [];
  const bands = buildScoreBands(latestRows);

  return {
    credit_invisible_count: toNotConnectedMetric('los', 'higher_is_better'),
    first_time_borrowers: toNotConnectedMetric('los', 'higher_is_better'),
    avg_loan_gap: toNotConnectedMetric('los', 'lower_is_better'),
    capacity_utilization: toNotConnectedMetric('los', 'higher_is_better'),
    underserved_bands: bands,
  };
}
